import { useState, useEffect } from 'react'
import { distance } from '@turf/distance'
import { point } from '@turf/helpers'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

export default function AddressSearchMap({ deals, selectedDeal, onSelectDeal, onClose }) {
  const [searchAddress, setSearchAddress] = useState('')
  const [searchCoords, setSearchCoords] = useState(null)
  const [nearbyDeals, setNearbyDeals] = useState([])
  const [radius] = useState(10) // miles
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [mapContainer, setMapContainer] = useState(null)
  const [map, setMap] = useState(null)

  // Initialize MapBox on mount
  useEffect(() => {
    if (!mapContainer) return

    const initMap = async () => {
      const mapboxgl = await import('mapbox-gl')
      mapboxgl.default.accessToken = MAPBOX_TOKEN

      const newMap = new mapboxgl.default.Map({
        container: mapContainer,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [-96.5, 38.5],
        zoom: 5,
      })

      setMap(newMap)

      return () => newMap.remove()
    }

    initMap()
  }, [mapContainer])

  // Update map when search results change
  useEffect(() => {
    if (!map || !searchCoords) return

    // Named handlers so we can detach on cleanup / before re-adding.
    const dealsClickHandler = (e) => {
      const feat = e.features?.[0]
      if (!feat) return
      const deal = deals.find(d => d.id === feat.properties.id)
      if (deal) onSelectDeal(deal)
    }
    const clusterClickHandler = (e) => {
      const feat = e.features?.[0]
      if (!feat) return
      const src = map.getSource('nearby-deals')
      src.getClusterExpansionZoom(feat.properties.cluster_id, (err, zoom) => {
        if (err) return
        map.easeTo({ center: feat.geometry.coordinates, zoom, duration: 500 })
      })
    }
    const dealsEnterHandler = () => { map.getCanvas().style.cursor = 'pointer' }
    const dealsLeaveHandler = () => { map.getCanvas().style.cursor = '' }

    const updateMap = () => {
      // Layer/source ids to tear down before rebuilding. Cluster layers share
      // the 'nearby-deals' source and must be removed before the source.
      const LAYER_IDS  = ['search-point', 'radius-circle',
                          'nearby-deals-clusters', 'nearby-deals-count', 'nearby-deals-point']
      const SOURCE_IDS = ['search-point', 'radius-circle', 'nearby-deals']

      LAYER_IDS.forEach(id => { if (map.getLayer(id)) map.removeLayer(id) })
      SOURCE_IDS.forEach(id => { if (map.getSource(id)) map.removeSource(id) })

      // Detach any lingering click/hover handlers from a prior search.
      map.off('click',      'nearby-deals-point',    dealsClickHandler)
      map.off('click',      'nearby-deals-clusters', clusterClickHandler)
      map.off('mouseenter', 'nearby-deals-point',    dealsEnterHandler)
      map.off('mouseleave', 'nearby-deals-point',    dealsLeaveHandler)
      map.off('mouseenter', 'nearby-deals-clusters', dealsEnterHandler)
      map.off('mouseleave', 'nearby-deals-clusters', dealsLeaveHandler)

      // Add search point
      map.addSource('search-point', {
        type: 'geojson',
        data: {
          type: 'Point',
          coordinates: [searchCoords.lng, searchCoords.lat],
        },
      })

      map.addLayer({
        id: 'search-point',
        type: 'circle',
        source: 'search-point',
        paint: {
          'circle-radius': 8,
          'circle-color': '#D4AF37',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#FFFFFF',
        },
      })

      // Add radius circle (approximate using circle layer)
      const radiusFeatures = []
      const steps = 64
      for (let i = 0; i < steps; i++) {
        const bearing = (i / steps) * 360
        const destination = calculateDestination(
          searchCoords.lat,
          searchCoords.lng,
          bearing,
          radius
        )
        radiusFeatures.push([destination.lng, destination.lat])
      }
      radiusFeatures.push(radiusFeatures[0])

      map.addSource('radius-circle', {
        type: 'geojson',
        data: {
          type: 'LineString',
          coordinates: radiusFeatures,
        },
      })

      map.addLayer({
        id: 'radius-circle',
        type: 'line',
        source: 'radius-circle',
        paint: {
          'line-color': '#D4AF37',
          'line-width': 2,
          'line-dasharray': [4, 4],
          'line-opacity': 0.6,
        },
      })

      // Add nearby deals — clustered source so overlapping / co-located deals
      // (portfolio addresses, sentinel-fallback coords) show as a single
      // "N" bubble whose count matches the list on the right.
      const dealFeatures = nearbyDeals.map(deal => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [deal.lng, deal.lat],
        },
        properties: {
          id: deal.id,
          name: deal.name,
          distance: deal.distance,
          deal_category: deal.deal_category,
        },
      }))

      map.addSource('nearby-deals', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: dealFeatures,
        },
        cluster:         true,
        clusterMaxZoom:  15,
        clusterRadius:   30,
      })

      // Cluster bubble
      map.addLayer({
        id: 'nearby-deals-clusters',
        type: 'circle',
        source: 'nearby-deals',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color':        'rgba(212,175,55,0.15)',
          'circle-stroke-color': 'rgba(212,175,55,0.5)',
          'circle-stroke-width': 1.5,
          'circle-radius':       ['step', ['get', 'point_count'], 14, 5, 17, 20, 20],
        },
      })

      // Cluster count label
      map.addLayer({
        id: 'nearby-deals-count',
        type: 'symbol',
        source: 'nearby-deals',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font':  ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size':  12,
        },
        paint: {
          'text-color': '#D4AF37',
        },
      })

      // Individual (unclustered) point
      map.addLayer({
        id: 'nearby-deals-point',
        type: 'circle',
        source: 'nearby-deals',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            12,
            8,
          ],
          'circle-color': [
            'match',
            ['get', 'deal_category'],
            'acquisition', '#3B82F6',
            'development', '#2ECC71',
            'lending',     '#D4AF37',
            '#94A3B8',
          ],
          'circle-stroke-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            2,
            1,
          ],
          'circle-stroke-color': '#FFFFFF',
        },
      })

      // Attach fresh handlers (previous ones were detached above)
      map.on('click',      'nearby-deals-point',    dealsClickHandler)
      map.on('click',      'nearby-deals-clusters', clusterClickHandler)
      map.on('mouseenter', 'nearby-deals-point',    dealsEnterHandler)
      map.on('mouseleave', 'nearby-deals-point',    dealsLeaveHandler)
      map.on('mouseenter', 'nearby-deals-clusters', dealsEnterHandler)
      map.on('mouseleave', 'nearby-deals-clusters', dealsLeaveHandler)

      // Fit bounds to show search point and radius
      map.flyTo({
        center: [searchCoords.lng, searchCoords.lat],
        zoom: 11
      })
    }

    // Wait for the base style to be ready before mutating sources/layers.
    if (map.isStyleLoaded()) {
      updateMap()
    } else {
      map.once('load', updateMap)
    }

    // Cleanup on unmount OR before next effect fires — prevents handler stacking.
    return () => {
      map.off('load', updateMap)
      if (!map.getStyle()) return   // map already removed (view unmounted)
      map.off('click',      'nearby-deals', dealsClickHandler)
      map.off('mouseenter', 'nearby-deals', dealsEnterHandler)
      map.off('mouseleave', 'nearby-deals', dealsLeaveHandler)
    }
  }, [map, searchCoords, nearbyDeals, deals, onSelectDeal])

  const handleSearch = async () => {
    if (!searchAddress.trim()) return

    setLoading(true)
    setError(null)

    try {
      // Geocode address using MapBox API
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchAddress)}.json?country=us&access_token=${MAPBOX_TOKEN}`
      )

      if (!response.ok) throw new Error('Geocoding failed')

      const data = await response.json()

      if (!data.features.length) {
        setError('Address not found')
        setLoading(false)
        return
      }

      const [lng, lat] = data.features[0].center
      setSearchCoords({ lat, lng, place: data.features[0].place_name })

      // Calculate distance to all deals
      const dealsWithDistance = deals
        .filter(d => d.lat != null && d.lng != null)
        .map(d => {
          const dealPoint = point([d.lng, d.lat])
          const searchPoint = point([lng, lat])
          const dist = distance(dealPoint, searchPoint, { units: 'miles' })
          return { ...d, distance: dist }
        })
        .filter(d => d.distance <= radius)
        .sort((a, b) => a.distance - b.distance)

      setNearbyDeals(dealsWithDistance)
    } catch (err) {
      setError(err.message)
    }

    setLoading(false)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Bar */}
      <div style={{
        padding: '12px 16px',
        background: '#061020',
        borderBottom: '1px solid rgba(212,175,55,0.2)',
        display: 'flex',
        gap: 8,
      }}>
        <input
          type="text"
          placeholder="Enter address (e.g., Denver, CO or 123 Main St)"
          value={searchAddress}
          onChange={(e) => setSearchAddress(e.target.value)}
          onKeyPress={handleKeyPress}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            color: '#FFFFFF',
            fontSize: 12,
            outline: 'none',
          }}
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{
            padding: '8px 16px',
            background: '#D4AF37',
            color: '#0A192F',
            border: 'none',
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 12,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div style={{
          padding: '8px 16px',
          background: 'rgba(231,76,60,0.1)',
          color: '#E74C3C',
          fontSize: 12,
          borderBottom: '1px solid rgba(231,76,60,0.2)',
        }}>
          {error}
        </div>
      )}

      {searchCoords && (
        <div style={{
          padding: '8px 16px',
          background: 'rgba(212,175,55,0.1)',
          color: '#D4AF37',
          fontSize: 11,
          borderBottom: '1px solid rgba(212,175,55,0.2)',
        }}>
          📍 {searchCoords.place} • {nearbyDeals.length} deals within {radius} miles
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Map */}
        <div
          ref={setMapContainer}
          style={{ flex: 2, minWidth: 0 }}
        />

        {/* Nearby Deals List */}
        {searchCoords && (
          <div style={{
            flex: 1,
            minWidth: 0,
            background: '#0A192F',
            borderLeft: '1px solid rgba(212,175,55,0.2)',
            overflow: 'auto',
          }}>
            <div style={{
              padding: '12px 16px',
              fontWeight: 600,
              color: '#D4AF37',
              fontSize: 12,
              borderBottom: '1px solid rgba(212,175,55,0.2)',
              position: 'sticky',
              top: 0,
              background: '#061020',
            }}>
              NEARBY DEALS ({nearbyDeals.length})
            </div>

            {nearbyDeals.length === 0 ? (
              <div style={{
                padding: 16,
                color: '#94A3B8',
                fontSize: 12,
                textAlign: 'center',
              }}>
                No deals within {radius} miles
              </div>
            ) : (
              nearbyDeals.map(deal => (
                <div
                  key={deal.id}
                  onClick={() => onSelectDeal(deal)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    cursor: 'pointer',
                    background: selectedDeal?.id === deal.id ? 'rgba(212,175,55,0.1)' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(212,175,55,0.15)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selectedDeal?.id === deal.id ? 'rgba(212,175,55,0.1)' : 'transparent'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    marginBottom: 4,
                  }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: deal.type === 'acquisition' ? '#3B82F6' : deal.type === 'development' ? '#2ECC71' : '#D4AF37',
                    }} />
                    <span style={{
                      flex: 1,
                      color: '#FFFFFF',
                      fontSize: 11,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {deal.name}
                    </span>
                  </div>
                  <div style={{
                    color: '#94A3B8',
                    fontSize: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span>{[deal.city, deal.state].filter(Boolean).join(', ') || '–'}</span>
                    <span style={{ color: '#D4AF37', fontWeight: 600 }}>
                      {deal.distance.toFixed(1)} mi
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Helper: Calculate destination point given distance and bearing
function calculateDestination(lat, lng, bearing, distanceMiles) {
  const R = 3959 // Earth radius in miles
  const lat1 = (lat * Math.PI) / 180
  const lon1 = (lng * Math.PI) / 180
  const brng = (bearing * Math.PI) / 180

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distanceMiles / R) +
    Math.cos(lat1) * Math.sin(distanceMiles / R) * Math.cos(brng)
  )
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(distanceMiles / R) * Math.cos(lat1),
      Math.cos(distanceMiles / R) - Math.sin(lat1) * Math.sin(lat2)
    )

  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (lon2 * 180) / Math.PI,
  }
}
